import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Drawer } from '@/common/components/Drawer/Drawer';
import { Icon } from '@/common/components/Icons/Icon';
import { LabeledValueList } from '@/common/components/LabeledValueList/LabeledValueList';
import { Stack } from '@/common/components/Stack/Stack';
import { Tooltip } from '@/common/components/Tooltip/Tooltip';
import { MARKET } from '@/common/constants/markets';
import { useMarket } from '@/common/hooks/useMarket';
import {
  IInsuranceMember,
  IInsurancePolicy,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { formatDate } from '@/common/utils/date';
import { MoneyUtils, defaultMoney } from '@coverflex-tech/hypernova-utils';
import { StatusBadge } from '../PolicyPeopleList.utils';
import { useGetCoveredPersonActions } from './PolicyPeopleDrawer.utils';

export function PolicyPeopleDrawer({
  policy,
  person,
  onClose,
}: TPolicyPeopleDrawerProps) {
  const { t } = useTranslation();
  const { slug } = useMarket();

  const isUpcomingPolicy = policy.status === 'drafted';
  const coveredPersonActions = useGetCoveredPersonActions(person, policy);

  return (
    <Drawer
      title={person.shortName}
      icon={<Icon icon="user" />}
      iconBadgeColor="accentSecondary20"
      onClose={onClose}
      footer={coveredPersonActions}
    >
      <Stack gap="md">
        <View className="self-start">
          <StatusBadge
            person={person}
            badgeProps={{ emphasis: 'normal', size: 'lg' }}
          />
        </View>
        <LabeledValueList.Root title="">
          {person.type !== 'employee' && (
            <LabeledValueList.Item
              label={t('general.labels.kinship_degree')}
              value={t(`general.kinship.${person.type}`)}
            />
          )}
          {person.cardNumber && (
            <LabeledValueList.Item
              label={t('general.labels.card_number')}
              value={person.cardNumber}
            />
          )}
          <LabeledValueList.Item
            label={t('dashboard.insurance_detail.table_headers.inclusion_date')}
            value={person.startsOn ? formatDate(person.startsOn) : '—'}
          />
          {person.endsOn && (
            <LabeledValueList.Item
              label={t('dashboard.insurance_detail.table_headers.removal_date')}
              value={formatDate(person.endsOn)}
            />
          )}
        </LabeledValueList.Root>
        <LabeledValueList.Root title="">
          <LabeledValueList.Item
            label={
              <Stack horizontal align="center" gap="sm">
                <BodyText as="span">
                  {t('dashboard.insurance_detail.table_headers.paid_by_you')}
                </BodyText>
                {isUpcomingPolicy && (
                  <Tooltip
                    side="top"
                    content={t(
                      'dashboard.insurance_detail.table_headers.payment_tooltip'
                    )}
                  >
                    <Icon icon={['far', 'circle-info']} />
                  </Tooltip>
                )}
              </Stack>
            }
            value={MoneyUtils.moneyToString(
              person.cost?.paidByEmployee ?? defaultMoney
            )}
          />
          <LabeledValueList.Item
            label={
              <Stack horizontal align="center" gap="sm">
                <BodyText as="span">
                  {t(
                    'dashboard.insurance_detail.table_headers.paid_by_company'
                  )}
                </BodyText>
                {isUpcomingPolicy && (
                  <Tooltip
                    side="top"
                    content={t(
                      'dashboard.insurance_detail.table_headers.payment_tooltip'
                    )}
                  >
                    <Icon icon={['far', 'circle-info']} />
                  </Tooltip>
                )}
              </Stack>
            }
            value={MoneyUtils.moneyToString(
              person.cost?.paidByCompany ?? defaultMoney
            )}
          />
          {slug === MARKET.PT && (
            <LabeledValueList.Item
              label={t(
                'dashboard.insurance_detail.table_headers.payment_method'
              )}
              value={
                person.paymentType === 'company'
                  ? '—'
                  : t(
                      `dashboard.insurance_detail.paid_by.${person.paymentType}`
                    )
              }
            />
          )}
          {slug === MARKET.ES && (
            <LabeledValueList.Item
              label={t('dashboard.insurance_detail.table_headers.taxed_amount')}
              value={MoneyUtils.formatMoney(
                person.metadata?.nonExemptAmount ?? defaultMoney
              )}
            />
          )}
        </LabeledValueList.Root>
      </Stack>
    </Drawer>
  );
}

export type TPolicyPeopleDrawerProps = {
  policy: IInsurancePolicy;
  person: IInsuranceMember;
  onClose: () => void;
};
